import { useAuth } from './auth'

/**
 * Thin fetch wrapper around the briefer-api. Reads the bearer token from
 * the auth store on each request so a re-login picks up automatically.
 *
 * Base URL defaults to localhost:7072 in dev (briefer-api default port).
 * In production the SPA is served from the same origin as the API; the
 * empty string base means relative paths.
 */
export const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:7072'
).replace(/\/$/, '')

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    message?: string,
  ) {
    super(message ?? `API ${status}: ${body || 'request failed'}`)
    this.name = 'ApiError'
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuth.getState().token
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new ApiError(response.status, text)
  }
  if (response.status === 204) return undefined as T
  const ct = response.headers.get('content-type') ?? ''
  if (!ct.includes('application/json')) return undefined as T
  return (await response.json()) as T
}

// ───────────────────────── Domain types ────────────────────────────────────

export interface WhoAmI {
  sub: string
  email?: string
  partner_id: string
  roles: string[]
}

export type BriefStatus = 'DRAFT' | 'READY' | 'LOCKED' | 'ARCHIVED'

export interface BriefSummary {
  id: string
  app_name: string | null
  status: BriefStatus
  readiness_score: number | null
  readiness_grade: string | null
  created_at: string
  updated_at: string
  current_revision: number
}

export interface BriefRevision {
  revision_number: number
  author: string
  created_at: string
  change_note: string | null
  data: Record<string, unknown>
}

export interface BriefDetail extends BriefSummary {
  data: Record<string, unknown>
  revisions: BriefRevision[]
}

export interface Collaborator {
  user_id: string
  email: string
  role: 'viewer' | 'editor'
}

export interface DeliverableLink {
  audience: string
  format: string
  url: string
  filename: string
}

export interface PartnerRow {
  id: string
  name: string
  slug: string
  created_at: string
}

export interface UserRow {
  id: string
  email: string
  partner_id: string
  roles: string[]
  created_at: string
}

export interface PptxTemplateRow {
  id: string
  audience: 'executive' | 'sales' | 'end_user' | 'training'
  filename: string
  uploaded_at: string
}

export interface WebhookDispatchRow {
  id: string
  brief_id: string
  url: string
  status: 'pending' | 'delivered' | 'failed'
  attempts: number
  last_attempted_at: string | null
  response_status: number | null
}
